import axios, { type AxiosInstance } from 'axios';
import type { AuthEvents, TokenStorage } from './storage';

export interface APIClientOptions {
  /** Fully-resolved API base URL (computed per-app; the client stays env-agnostic). */
  baseURL: string;
  /** Token persistence adapter (localStorage on web, secure-store on mobile). */
  storage: TokenStorage;
  /** Platform reaction when the session is unrecoverable. */
  authEvents: AuthEvents;
}

// Auth endpoints that must NOT carry an Authorization header and must never
// trigger the refresh-and-retry flow. Kept identical to the previous web client.
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
] as const;

function isAuthUrl(url?: string): boolean {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Platform-agnostic HTTP transport: a configured axios instance whose request
 * interceptor attaches the bearer token and whose response interceptor performs
 * transparent token refresh on 401 — using the injected {@link TokenStorage}
 * and {@link AuthEvents} instead of localStorage/window.
 *
 * Behaviour is a 1:1 port of the original web interceptor; only the token
 * source (storage) and the unauthorized reaction (authEvents) are abstracted.
 */
export class APIClient {
  public readonly client: AxiosInstance;

  private readonly storage: TokenStorage;
  private readonly authEvents: AuthEvents;

  constructor(options: APIClientOptions) {
    this.storage = options.storage;
    this.authEvents = options.authEvents;

    this.client = axios.create({
      baseURL: options.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor: attach the access token (except on public auth endpoints).
    this.client.interceptors.request.use(
      async (config) => {
        if (!isAuthUrl(config.url)) {
          const token = await this.storage.getAccess();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor: on 401, refresh once and retry the original request.
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Never attempt refresh when the failing request is itself an auth endpoint.
        const isAuthRequest = isAuthUrl(originalRequest?.url);

        if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
          originalRequest._retry = true;

          try {
            const refreshToken = await this.storage.getRefresh();
            if (refreshToken) {
              const response = await this.client.post('/auth/refresh', {
                refresh_token: refreshToken,
              });
              const { access_token, refresh_token: newRefreshToken } = response.data;
              await this.storage.setTokens(access_token, newRefreshToken);
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            await this.storage.clear();
            this.authEvents.onUnauthorized();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );
  }
}
